/**
 * AP-PO-01: Purchase Order Detail Page
 * Shows full PO detail, lines, timeline, and approval workflow actions.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  CheckCircle2,
  Clock,
  Package,
  XCircle,
  Send,
  ThumbsUp,
  Zap,
  Ban,
  AlertTriangle,
  Lock,
  Archive,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  DRAFT: { label: "Draft", color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: FileText },
  PENDING_APPROVAL: { label: "Pending Approval", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  APPROVED: { label: "Approved", color: "bg-blue-100 text-blue-800 border-blue-200", icon: CheckCircle2 },
  ISSUED: { label: "Issued", color: "bg-green-100 text-green-800 border-green-200", icon: Package },
  CLOSED: { label: "Closed", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Archive },
  CANCELLED: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
};

function formatAmount(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-700", icon: AlertTriangle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${cfg.color}`}>
      <Icon size={13} />
      {cfg.label}
    </span>
  );
}

// ── Confirm Action Dialog ─────────────────────────────────────────────────────

function ActionDialog({ open, onClose, title, description, onConfirm, loading, requireReason, reasonLabel = "Notes (optional)" }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600">{description}</p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={requireReason ? `${reasonLabel} (required)` : reasonLabel}
          rows={3}
          data-testid="action-dialog-reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={() => onConfirm(reason)}
            disabled={loading || (requireReason && !reason.trim())}
            data-testid="action-dialog-confirm-btn"
          >
            {loading ? "Processing…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Timeline Entry ────────────────────────────────────────────────────────────

function TimelineEntry({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div className={`flex flex-col gap-0.5 p-2 rounded-lg ${highlight ? "bg-green-50" : "bg-zinc-50"}`}>
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={`text-sm font-medium ${highlight ? "text-green-800" : "text-zinc-800"}`}>{value}</span>
    </div>
  );
}

// ── Main Detail Page ──────────────────────────────────────────────────────────

export default function PurchaseOrderDetail() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSnapshot, setShowSnapshot] = useState(false);

  const [dialogs, setDialogs] = useState({
    submit: false,
    approve: false,
    issue: false,
    cancel: false,
    close: false,
  });

  const openDialog = (name) => setDialogs((d) => ({ ...d, [name]: true }));
  const closeDialogs = () => setDialogs({ submit: false, approve: false, issue: false, cancel: false, close: false });

  const fetchPO = async () => {
    try {
      const res = await axios.get(`${API}/api/ap/purchase-orders/${poId}`, { withCredentials: true });
      setPO(res.data);
    } catch {
      toast.error("Failed to load Purchase Order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPO(); }, [poId]);

  const doTransition = async (action, notes) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/ap/purchase-orders/${poId}/${action}`,
        { reason: notes, notes },
        { withCredentials: true }
      );
      toast.success(`PO ${action.replace("-", " ")} successful`);
      closeDialogs();
      await fetchPO();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} PO`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-zinc-400">
        <Clock size={18} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-400 space-y-2">
        <AlertTriangle size={32} />
        <p>Purchase Order not found</p>
        <Button variant="outline" onClick={() => navigate("/finance/purchase-orders")}>Back to POs</Button>
      </div>
    );
  }

  const canSubmit = po.status === "DRAFT";
  const canApprove = po.status === "PENDING_APPROVAL";
  const canIssue = po.status === "APPROVED";
  const canCancel = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "ISSUED"].includes(po.status);
  const canClose = po.status === "ISSUED";

  const subtotal = (po.subtotal_minor || 0) / 100;
  const tax = (po.tax_amount_minor || 0) / 100;
  const total = (po.total_minor || 0) / 100;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="po-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate("/finance/purchase-orders")}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors mb-3"
            data-testid="po-back-btn"
          >
            <ArrowLeft size={14} /> Purchase Orders
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-zinc-900 font-mono" data-testid="po-number-display">
                  {po.po_number || <span className="text-zinc-400 font-sans text-lg">Draft PO</span>}
                </h1>
                <StatusBadge status={po.status} />
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                Created {new Date(po.created_at).toLocaleDateString("en-IN")}
                {po.issued_at && ` · Issued ${new Date(po.issued_at).toLocaleDateString("en-IN")}`}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2" data-testid="po-action-area">
              {canSubmit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDialog("submit")}
                  data-testid="po-submit-btn"
                >
                  <Send size={13} className="mr-1.5" /> Submit for Approval
                </Button>
              )}
              {canApprove && (
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => openDialog("approve")}
                  data-testid="po-approve-btn"
                >
                  <ThumbsUp size={13} className="mr-1.5" /> Approve
                </Button>
              )}
              {canIssue && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => openDialog("issue")}
                  data-testid="po-issue-btn"
                >
                  <Zap size={13} className="mr-1.5" /> Issue to Supplier
                </Button>
              )}
              {canClose && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDialog("close")}
                  data-testid="po-close-btn"
                >
                  <Archive size={13} className="mr-1.5" /> Close PO
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => openDialog("cancel")}
                  data-testid="po-cancel-btn"
                >
                  <Ban size={13} className="mr-1.5" /> Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {/* Issued alert */}
        {po.status === "ISSUED" && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="po-issued-banner">
            <Lock size={18} className="text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">
                PO {po.po_number} has been formally issued to the supplier.
              </p>
              <p className="text-xs text-green-600 mt-0.5">
                Commercial terms are locked. The supplier can now view this PO and submit invoices against it.
              </p>
            </div>
          </div>
        )}

        {/* 3-col summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Supplier</h3>
            <div className="flex items-start gap-2">
              <Building2 size={14} className="text-zinc-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-zinc-800 text-sm" data-testid="po-supplier-name">
                  {po.supplier?.display_name || po.supplier?.company_name || po.supplier_name_snapshot || "—"}
                </p>
                {po.supplier?.gst_ref && (
                  <p className="text-xs text-zinc-400">GST: {po.supplier.gst_ref}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Event</h3>
            <div className="flex items-start gap-2">
              <Calendar size={14} className="text-zinc-400 mt-0.5 flex-shrink-0" />
              <p className="font-semibold text-zinc-800 text-sm" data-testid="po-event-name">
                {po.event?.event_name || po.event?.name || po.event_name_snapshot || "—"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Amount</h3>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Tax</span>
                <span>₹{tax.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between font-bold text-sm text-zinc-800 border-t border-zinc-100 pt-1">
                <span>Total ({po.currency_code})</span>
                <span data-testid="po-total-amount">{formatAmount(po.total_minor, po.currency_code)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">Workflow Timeline</h3>
          <div className="grid grid-cols-4 gap-2">
            <TimelineEntry label="Created" value={new Date(po.created_at).toLocaleDateString("en-IN")} />
            <TimelineEntry label="Submitted" value={po.submitted_at ? new Date(po.submitted_at).toLocaleDateString("en-IN") : null} />
            <TimelineEntry label="Approved" value={po.approved_at ? new Date(po.approved_at).toLocaleDateString("en-IN") : null} />
            <TimelineEntry label="Issued" value={po.issued_at ? new Date(po.issued_at).toLocaleDateString("en-IN") : null} highlight={!!po.issued_at} />
          </div>
          {po.notes && (
            <p className="mt-3 text-sm text-zinc-600 bg-zinc-50 rounded-lg p-3 italic">
              {po.notes}
            </p>
          )}
        </div>

        {/* Lines */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-800">
              Line Items
              <span className="ml-2 text-xs font-normal text-zinc-400">
                ({po.lines?.length || 0} {po.lines?.length === 1 ? "item" : "items"})
              </span>
            </h3>
          </div>
          {po.lines?.length > 0 ? (
            <table className="w-full text-sm" data-testid="po-lines-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500">Description</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-zinc-500">Qty</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-zinc-500">Unit Rate</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-zinc-500">Tax</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-zinc-500">Line Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {po.lines.map((line, i) => (
                  <tr key={line.id || i} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <p className="text-zinc-800 font-medium">{line.description}</p>
                      {line.delivery_milestone && (
                        <p className="text-xs text-zinc-400 mt-0.5">Milestone: {line.delivery_milestone}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-600">
                      {line.quantity} {line.unit}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-600">
                      {formatAmount(line.unit_rate_minor)}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-500 text-xs">
                      {line.tax_rate_bps ? `${(line.tax_rate_bps / 100).toFixed(0)}%` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-zinc-800">
                      {formatAmount(line.line_total_with_tax_minor)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-zinc-200 bg-zinc-50">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-zinc-600">
                    Total ({po.currency_code})
                  </td>
                  <td className="px-4 py-3 text-right text-base font-bold text-zinc-900">
                    {formatAmount(po.total_minor, po.currency_code)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="flex items-center justify-center h-24 text-zinc-400 text-sm">
              No line items
            </div>
          )}
        </div>

        {/* Immutable snapshot (ISSUED+) */}
        {po.po_snapshot && (
          <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition-colors"
              onClick={() => setShowSnapshot((s) => !s)}
              data-testid="po-snapshot-toggle"
            >
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-green-600" />
                <span className="text-sm font-semibold text-green-800">
                  Immutable Issued Snapshot
                </span>
                <span className="text-xs text-green-600 bg-green-100 border border-green-200 rounded px-2 py-0.5">
                  LOCKED
                </span>
              </div>
              {showSnapshot ? <ChevronUp size={14} className="text-green-600" /> : <ChevronDown size={14} className="text-green-600" />}
            </button>
            {showSnapshot && (
              <div className="p-4">
                <pre className="text-xs bg-zinc-50 rounded-lg p-4 overflow-x-auto text-zinc-600 border border-zinc-200">
                  {JSON.stringify(po.po_snapshot, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Dialogs */}
      <ActionDialog
        open={dialogs.submit}
        onClose={closeDialogs}
        title="Submit PO for Approval"
        description="This will submit the Purchase Order for internal approval. A different user must approve it."
        onConfirm={(notes) => doTransition("submit", notes)}
        loading={actionLoading}
        reasonLabel="Submission notes (optional)"
      />
      <ActionDialog
        open={dialogs.approve}
        onClose={closeDialogs}
        title="Approve Purchase Order"
        description="Confirm you have reviewed the commercial terms and approve this PO. You must not be the PO creator."
        onConfirm={(notes) => doTransition("approve", notes)}
        loading={actionLoading}
        reasonLabel="Approval notes (optional)"
      />
      <ActionDialog
        open={dialogs.issue}
        onClose={closeDialogs}
        title="Issue PO to Supplier"
        description="Issuing will assign an immutable PO number and make this PO visible to the supplier. This action locks all commercial terms."
        onConfirm={(notes) => doTransition("issue", notes)}
        loading={actionLoading}
        reasonLabel="Issue notes (optional)"
      />
      <ActionDialog
        open={dialogs.cancel}
        onClose={closeDialogs}
        title="Cancel Purchase Order"
        description="Provide a reason for cancellation. This cannot be undone."
        onConfirm={(notes) => doTransition("cancel", notes)}
        loading={actionLoading}
        requireReason
        reasonLabel="Cancellation reason"
      />
      <ActionDialog
        open={dialogs.close}
        onClose={closeDialogs}
        title="Close Purchase Order"
        description="Closing marks this PO as fully delivered. No further invoices can be submitted."
        onConfirm={(notes) => doTransition("close", notes)}
        loading={actionLoading}
        reasonLabel="Closing notes (optional)"
      />
    </div>
  );
}
